import React, {
  FunctionComponent,
  useEffect,
  SyntheticEvent,
  useState,
} from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { Container, Grid } from 'semantic-ui-react';
import { ApolloError } from 'apollo-boost';
import { useMutation } from '@apollo/react-hooks';
import { useRouteMatch } from 'react-router-dom';
import { GraphQLError } from 'graphql';
import withStyles from 'react-jss';
import classnames from 'classnames';
import { pathOr } from 'ramda';
import { endOfMonth, format } from 'date-fns';

import { ClassProp } from 'helpers/types';
import { toastMapping } from 'components/ErrorBoundary/ErrorBoundary';
import FormSelectList from 'components/FormComponents/FormSelect';
import FormDateInput from 'components/FormComponents/FormDateInput';
import FormInput from 'components/FormComponents/FormInput';
import FormTimeInput from 'components/FormComponents/FormTimeInput';
import {
  AdvertiserTypeOptions,
  BudgetTypes,
} from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantOffers/MerchantOffer.helpers';
import {
  buildTimeStamp,
  dateIsAfterCurrentDate,
  cstToUTC,
  midnight,
  oneMinuteToMidnight,
  checkDates,
  getCurrentCSTDateWithTime,
} from 'helpers/date';
import {
  BudgetType,
  CampaignFormData,
  AdvertiserType,
  PulseSegmentResponse,
  MatchParams,
} from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantOffers/MerchantOffer.types';

import GET_CAMPAIGNS_FOR_MERCHANT from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantOffers/scenes/CreateOfferModal/CreateOffer.queries';
import { formatDate } from 'helpers/date';
import { Currency } from 'helpers/currency';

import CAMPAIGN_CREATE from './CreateCampaign.mutations';
import styles from './CreateCampaign.styles';
import MonthlyBudgetsInput from './components/MonthlyBudgetsInput';
import { processBudgets } from './CreateCampaign.helpers';

type Props = {
  classes: ClassProp<ReturnType<typeof styles>>;
  modalClose?: () => void;
};

const getApolloError = (errors?: ApolloError | GraphQLError[]) =>
  pathOr('', ['graphQLErrors'], errors);

const defaultSuccessMessage = [
  {
    displayMessage: 'Success!',
    message: 'Campaign Created!',
  },
];

const CreateCampaign: FunctionComponent<Props> = ({ classes, modalClose }) => {
  const match = useRouteMatch();
  const { merchantId, merchantName } = match.params as MatchParams;

  const currentDate = new Date();

  const methods = useForm<CampaignFormData>({
    mode: 'onBlur',
    defaultValues: {
      campaignStartDt: currentDate,
      campaignStartTm: format(midnight(), 'HH:mm'),
      campaignEndTm: format(oneMinuteToMidnight(), 'HH:mm'),
      approvalDate: currentDate,
    },
  });

  const { register, handleSubmit, getValues, watch, trigger } = methods;

  const [showMonthlyBudgetInput, setShowMonthlyBudgetInput] = useState(false);

  const handleError = (error: any) => {
    toastMapping({}, 'error')(getApolloError(error) as any);
  };
  const handleCampaignCreateResponse = (response: PulseSegmentResponse) => {
    const id = pathOr(null, ['campaignCreate', 'id'], response);

    if (id) {
      toastMapping({}, 'success')(defaultSuccessMessage as any);
      if (modalClose) modalClose();
    }
  };

  const handleClick = (_: SyntheticEvent<HTMLButtonElement, Event>) => {
    if (modalClose) modalClose();
  };

  const [runMutation, { loading: isSubmitting }] = useMutation(
    CAMPAIGN_CREATE,
    {
      onCompleted: handleCampaignCreateResponse,
      onError: handleError,
      refetchQueries: [
        {
          query: GET_CAMPAIGNS_FOR_MERCHANT,
          variables: {
            params: {
              merchantUuid: merchantId,
            },
          },
        },
      ],
    },
  );

  useEffect(() => {
    register({ name: 'budgetType' });
    register({ name: 'budgetAdvertiserType' });
    register(
      { name: 'approvalDate' },
      { required: 'Approval Date is required' },
    );

    register(
      { name: 'campaignStartDt' },
      {
        required: 'Start Date is required',
        validate: {
          oneOf: () =>
            getValues().campaignEndDt === undefined ||
            checkDates(
              getValues().campaignStartDt,
              getValues().campaignEndDt,
            ) ||
            'Start Date must come before End Date',
          isAfter: (value) =>
            dateIsAfterCurrentDate(
              getCurrentCSTDateWithTime(value, getValues().campaignStartTm),
            ) || "Start Date can't be earlier than today",
        },
      },
    );

    register(
      { name: 'campaignEndDt' },
      {
        required: 'End Date is required',
        validate: {
          oneOf: () =>
            checkDates(
              getValues().campaignStartDt,
              getValues().campaignEndDt,
            ) || 'End Date must come after Start Date',
          isAfter: (value) =>
            dateIsAfterCurrentDate(value) ||
            "End Date can't be earlier than today",
        },
      },
    );

    register(
      { name: 'campaignStartTm' },
      { required: 'Start Time is required' },
    );
    register({ name: 'campaignEndTm' }, { required: 'End Time is required' });
  }, [register]);

  const watchFields = watch([
    'campaignEndDt',
    'budgetAmount',
    'campaignStartDt',
    'campaignStartTm',
    'budgetType',
  ]);

  useEffect(() => {
    trigger('campaignStartDt');
  }, [watchFields.campaignStartTm]);

  const onSubmit = ({
    campaignStartDt,
    campaignStartTm,
    campaignEndDt,
    campaignEndTm,
    budgetName,
    budgetAmount,
    budgetType,
    budgetAdvertiserType,
    approvalDate,
    budgets,
    ...data
  }: CampaignFormData) => {
    const startTimeStamp = cstToUTC(
      buildTimeStamp(campaignStartDt, campaignStartTm),
    );
    const endTimeStamp = cstToUTC(buildTimeStamp(campaignEndDt, campaignEndTm));
    const budget: any = {
      budgetType,
      budgetName,
      budgetAmount: {
        value: parseInt((budgetAmount as unknown) as string),
        currency: Currency.USD,
      },
      approvalDate: formatDate(approvalDate),
    };

    if (budgetAdvertiserType === AdvertiserType.MARKETPLACE) {
      budget.advertiser = {
        name: 'DOSH',
      };
    }

    const campaign = {
      campaignStart: startTimeStamp,
      campaignEnd: endTimeStamp,
      budgets:
        budgetType === BudgetType.FIXED_AMOUNT_FIXED_DATE_RANGE
          ? processBudgets(
              budgets,
              budgetName,
              budgetType,
              approvalDate,
              startTimeStamp,
              endTimeStamp,
              budgetAdvertiserType,
            )
          : { ...budget },
      insertionOrderAmount: {
        value: parseInt((budgetAmount as unknown) as string),
        currency: Currency.USD,
      },
      ...data,
    };

    runMutation({
      variables: {
        params: { ...campaign },
      },
    });

    return;
  };

  const handleBudgetTypeSelection = (value: string | number | boolean) => {
    setShowMonthlyBudgetInput(
      value === BudgetType.FIXED_AMOUNT_FIXED_DATE_RANGE,
    );
  };

  const endDateChangeHandler = () => {
    trigger('campaignStartDt');
  };

  return (
    <Container className={classes.container}>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <input
            type="hidden"
            value={merchantId}
            name="advertiserMerchantUuid"
            ref={register}
          />
          <input
            type="hidden"
            value={merchantName}
            name="advertiserName"
            ref={register}
          />
          <input
            type="hidden"
            value={AdvertiserType.MERCHANT}
            name="advertiserType"
            ref={register}
          />
          <Grid>
            <Grid.Row>
              <Grid.Column width={7}>
                <FormInput
                  name="campaignName"
                  type="text"
                  placeholder="Enter a name for this Campaign"
                  label="Campaign Name"
                  register={register({
                    required: 'Campaign name is required.',
                  })}
                />
              </Grid.Column>
              <Grid.Column width={7}>
                <FormInput
                  name="insertionOrderRef"
                  type="text"
                  placeholder="Enter the insertion Order Ref"
                  label="Insertion Order Ref"
                  register={register({
                    required: 'Insertion Order is required.',
                  })}
                />
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column width={4}>
                <FormDateInput
                  name="campaignStartDt"
                  label="Start Date"
                  placeholder={currentDate}
                  value={watchFields.campaignStartDt}
                />
              </Grid.Column>
              <Grid.Column width={4}>
                <FormTimeInput
                  name="campaignStartTm"
                  label="Start Time (CST)"
                  useCST={false}
                  placeholder={midnight()}
                />
              </Grid.Column>
              <Grid.Column width={4}>
                <FormDateInput
                  name="campaignEndDt"
                  label="End Date"
                  onDateChange={endDateChangeHandler}
                  placeholder={
                    watchFields.budgetType ===
                    BudgetType.FIXED_AMOUNT_FIXED_DATE_RANGE
                      ? endOfMonth(currentDate)
                      : new Date('2036-01-01')
                  }
                />
              </Grid.Column>
              <Grid.Column width={4}>
                <FormTimeInput
                  name="campaignEndTm"
                  label="End Time (CST)"
                  useCST={false}
                  placeholder={oneMinuteToMidnight()}
                />
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column width={4}>
                <FormInput
                  name="budgetName"
                  type="text"
                  placeholder="Enter a name for this budget"
                  label="Budget Name"
                  register={register({ required: 'Budget name is required.' })}
                />
              </Grid.Column>
              <Grid.Column width={4}>
                <FormInput
                  name="budgetAmount"
                  type="number"
                  placeholder="Enter the amount for the budget"
                  label="Budget Amount"
                  register={register({
                    required: 'Budget amount is required.',
                    validate: {
                      oneOf: (value: string) =>
                        parseFloat(value) !== NaN || 'additionalMsg1',
                    },
                  })}
                />
              </Grid.Column>
              <Grid.Column width={4}>
                <FormSelectList
                  label="Budget type"
                  name="budgetType"
                  onSelectChange={handleBudgetTypeSelection}
                  dropDownOptions={BudgetTypes}
                  defaultValue={null}
                />
              </Grid.Column>
            </Grid.Row>
            {showMonthlyBudgetInput && (
              <MonthlyBudgetsInput
                name="budgets"
                campaignEndDate={new Date(watchFields.campaignEndDt)}
                totalBudgetAmount={watchFields.budgetAmount}
                campaignStartDate={new Date(watchFields.campaignStartDt)}
              />
            )}
            <Grid.Row>
              <Grid.Column width={4}>
                <FormSelectList
                  label="Budget Advertiser"
                  name="budgetAdvertiserType"
                  dropDownOptions={AdvertiserTypeOptions}
                  defaultValue={null}
                />
              </Grid.Column>
              <Grid.Column width={4}>
                <FormDateInput
                  name="approvalDate"
                  label="Approval Date"
                  value={currentDate}
                />
              </Grid.Column>
            </Grid.Row>
            <Grid.Row className={classes.formRow}>
              <button
                className={classnames(
                  classes.button,
                  classes.buttonSecondary,
                  classes.mediumBtn,
                )}
                onClick={handleClick}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className={classnames(classes.button, classes.mediumBtn)}
              >
                {isSubmitting ? '...Submitting' : 'Save'}
              </button>
            </Grid.Row>
          </Grid>
        </form>
      </FormProvider>
    </Container>
  );
};

export default withStyles(styles)(CreateCampaign);
